import { format } from 'date-fns';
import { Course } from '../types/elearning';
import { AppEvent } from '../types/event';
import { WPMedia } from '../types/wordpress';
import { stripHtml } from './htmlEntities';
import { getAttachmentFormat } from './resourceAttachments';

export function plain(value?: string | null) {
  if (!value) return '';
  return stripHtml(value).replace(/\s+/g, ' ').trim();
}

export function resourceWord(count: number) {
  return count === 1 ? '1 resource' : `${count} resources`;
}

export function hubWord(count: number) {
  return count === 1 ? '1 toolkit' : `${count} toolkits`;
}

export function insideWord(sectionCount: number, hubCount: number) {
  const total = sectionCount + hubCount;
  if (hubCount > 0 && sectionCount === 0) return hubWord(hubCount);
  if (sectionCount > 0 && hubCount === 0) {
    return sectionCount === 1 ? '1 section' : `${sectionCount} sections`;
  }
  return total === 1 ? '1 inside' : `${total} inside`;
}

function namesCoverage(sentence: string) {
  const standard = /\b(protocols?|standards?|recommendations?|guidelines?|regulations?)\b/i.test(sentence);
  const covers = /\b(hospitals?|patients?|ontario|clinicians?|providers?|populations?|staff)\b/i.test(sentence);
  return standard && covers;
}

function authorListOnly(text: string) {
  return !text.split(/\s+/).some((word) => /^[a-z]{4,}/.test(word.replace(/[^a-z]/gi, '')));
}

export function resourceClinical(excerpt: string) {
  const text = plain(excerpt);
  if (!text) return '';
  if (/\b(pmid\b|doi\s*:|doi\.org)/i.test(text)) return '';
  const sentences = text.split(/(?<=[.!?])\s+/).map((sentence) => sentence.trim()).filter(Boolean);
  const hit = sentences.find(namesCoverage);
  if (hit) return hit;
  if (authorListOnly(text)) return '';
  const prose = sentences.filter((sentence) => !/^(written by|authors?:)\b/i.test(sentence));
  return prose[0] ?? '';
}

export function resourceOffer(attachments: WPMedia[]) {
  if (attachments.length === 0) return '';
  if (attachments.length === 1) return getAttachmentFormat(attachments[0]).label;
  return `${attachments.length} files`;
}

export function eventStart(event: AppEvent) {
  return new Date(event.startAt);
}

export function clockLine(event: AppEvent) {
  const start = eventStart(event);
  if (event.allDay) return 'All day';
  const end = event.endAt ? new Date(event.endAt) : null;
  return `${format(start, 'h:mm a')}${end ? ` – ${format(end, 'h:mm a')}` : ''}`;
}

export function eventBlurb(excerpt: string) {
  let text = plain(excerpt).replace(/^@\s*/, '').replace(/^title:\s*/i, '');
  const cut = text.search(/\b(?:watch(?:\s+(?:archive|register))?|achieve|add to calendar|if you require a certificate|evaluation survey|you must register|https?:\/\/|www\.)/i);
  if (cut >= 0) text = text.slice(0, cut);
  text = text.replace(/\s+(?:watc\w*|achiev\w*|regist\w*|evaluat\w*)\s*$/i, '');
  text = text.replace(/\s+/g, ' ').trim();
  const split = text.match(/^(.*?)\s+presented\s+by\s*:?\s*(.*)$/i);
  if (!split) {
    if (!text || /^(watch|register|add to calendar|evaluation survey)$/i.test(text)) return { topic: '', presenter: '' };
    return { topic: text, presenter: '' };
  }
  return {
    topic: split[1].replace(/[\s:–—-]+$/, '').trim(),
    presenter: split[2].replace(/^[\s:]+/, '').replace(/[\s:–—-]+$/, '').trim(),
  };
}

export function eventTitle(event: AppEvent) {
  const start = eventStart(event);
  const forms = [
    format(start, 'MMMM d, yyyy'),
    format(start, 'MMMM d yyyy'),
    format(start, 'MMM d, yyyy'),
    format(start, 'MMMM do, yyyy'),
  ];
  let title = event.title.replace(/\s+/g, ' ').trim();
  for (const form of forms) {
    const escaped = form.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const trailing = new RegExp(`(?:\\s*[-–—]\\s*|\\s+)${escaped}\\.?\\s*$`, 'i');
    if (trailing.test(title)) {
      title = title.replace(trailing, '').replace(/[-–—]\s*$/, '').trim();
      break;
    }
  }
  return title;
}

export function courseLength(course: Pick<Course, 'estimatedTime' | 'modules'>) {
  const minutes = course.estimatedTime;
  const modules = course.modules?.length ?? 0;
  const time = minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)}h${minutes % 60 ? ` ${minutes % 60}m` : ''}`;
  const moduleLabel = modules === 1 ? '1 module' : `${modules} modules`;
  return { time, moduleLabel, line: modules > 0 ? `${time} · ${moduleLabel}` : time };
}

export function courseBlurb(course: Pick<Course, 'title' | 'description'>) {
  const original = plain(course.description);
  let text = original;
  const title = plain(course.title);
  let changed = false;
  if (title && text.slice(0, title.length).toLowerCase() === title.toLowerCase()) {
    text = text.slice(title.length);
    changed = true;
  }
  if (/\s*elearning module\.?\s*$/i.test(text)) {
    text = text.replace(/\s*elearning module\.?\s*$/i, '');
    changed = true;
  }
  if (!changed) return original;
  return text.replace(/^[\s:–—\-.,]+/, '').replace(/[\s.]+$/, '').trim();
}

export type CourseStage = 'open' | 'active' | 'done';

export function courseFact(course: Pick<Course, 'estimatedTime' | 'modules'>, stage: CourseStage, finished: number, progress: number) {
  const { time, line } = courseLength(course);
  const modules = course.modules?.length ?? 0;
  if (stage === 'active' && modules > 0) {
    const noun = modules === 1 ? 'module' : 'modules';
    const percent = Math.round(progress);
    const fromCount = Math.round((finished / modules) * 100);
    if (fromCount !== percent) return `${time} · ${percent}%`;
    return `${time} · ${finished} of ${modules} ${noun}`;
  }
  if (stage === 'active') return `${time} · ${Math.round(progress)}%`;
  return line;
}

export function courseStatus(stage: CourseStage) {
  if (stage === 'done') return { label: 'Completed', className: 'text-[#3F6E60]' };
  if (stage === 'active') return { label: 'In progress', className: 'text-primary' };
  return { label: 'Not enrolled', className: 'text-gray-600' };
}

export function courseFace(
  course: Pick<Course, 'modules'>,
  enrollment?: { status?: string; progress?: number; moduleCompletions?: unknown[] } | null,
) {
  const modules = course.modules?.length ?? 0;
  const status = enrollment?.status;
  if (!enrollment || !status || status === 'CANCELLED') {
    return { stage: 'open' as const, progress: 0, finished: 0 };
  }
  if (status === 'COMPLETED') {
    return { stage: 'done' as const, progress: 100, finished: modules };
  }
  const progress = enrollment.progress ?? 0;
  const recorded = enrollment.moduleCompletions;
  const finished =
    recorded != null
      ? recorded.length
      : modules > 0
        ? Math.min(modules, Math.round((progress / 100) * modules))
        : 0;
  return { stage: 'active' as const, progress, finished };
}
